import axios from "axios";

const inflight = new Map();
let globalCooldownUntil = 0;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const stableStringify = (value) => {
  if (!value || typeof value !== "object") return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  return `{${Object.keys(value)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(",")}}`;
};

export const getRetryAfterMs = (errOrResponse) => {
  const headers = errOrResponse?.response?.headers ?? errOrResponse?.headers;
  const retryAfter =
    headers?.["retry-after"] ??
    headers?.["Retry-After"] ??
    headers?.get?.("retry-after");

  if (!retryAfter) return null;

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);

  const date = Date.parse(retryAfter);
  return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
};

/**
 * Statuses that mean "the server is not there for a moment", not "this request
 * is wrong".
 *
 * A deploy reloads PHP-FPM, and until 2026-08-28 it did so with a stop/start
 * that closed the listening socket: for about a second Apache answered 503 to
 * everything, CORS preflights included, and a failed preflight makes the browser
 * abandon the request outright — so an app open at that moment lost every call
 * at once and showed an empty screen. The deploy no longer does that, but a
 * read worth retrying should not depend on the server never blinking.
 */
const TRANSIENT_STATUSES = [502, 503, 504];

const getStatus = (errOrResponse) =>
  errOrResponse?.response?.status ?? errOrResponse?.status ?? null;

const makeKey = ({ method = "GET", url, params, data }) =>
  `${method.toUpperCase()} ${url} ${stableStringify(params)} ${stableStringify(
    data,
  )}`;

const runReadWith429Retry = async (
  key,
  fn,
  { retries = 2, baseDelayMs = 600, maxDelayMs = 8000 } = {},
) => {
  if (inflight.has(key)) return inflight.get(key);

  const promise = (async () => {
    let attempt = 0;

    while (true) {
      const waitMs = globalCooldownUntil - Date.now();
      if (waitMs > 0) await sleep(Math.min(waitMs, maxDelayMs));

      try {
        return await fn();
      } catch (err) {
        const status = getStatus(err);
        const rateLimited = status === 429;
        const transient = TRANSIENT_STATUSES.includes(status);

        if (!rateLimited && !transient) throw err;

        if (attempt >= retries) {
          err.message = rateLimited
            ? "Troppe richieste, riprova tra poco."
            : "Servizio momentaneamente non disponibile. Riprova.";
          throw err;
        }

        const backoffMs = Math.min(
          maxDelayMs,
          baseDelayMs * Math.pow(2, attempt),
        );

        let retryMs;
        if (rateLimited) {
          retryMs = getRetryAfterMs(err) ?? backoffMs;
          // Only a rate limit is a reason to hold every other read back: the
          // allowance is shared, so racing on is what exhausted it.
          globalCooldownUntil = Math.max(
            globalCooldownUntil,
            Date.now() + retryMs,
          );
        } else {
          // A deploy ends for everybody in the same instant, so a fixed delay
          // would bring every client back in the same millisecond — straight
          // into the rate limit. Spread them out.
          retryMs = backoffMs + Math.random() * backoffMs * 0.5;
        }

        await sleep(retryMs);
        attempt += 1;
      }
    }
  })().finally(() => {
    if (inflight.get(key) === promise) inflight.delete(key);
  });

  inflight.set(key, promise);
  return promise;
};

export const axiosRead = (config, options) => {
  const method = config?.method ?? "GET";
  const baseKey = makeKey({
    method,
    url: config?.url,
    params: config?.params,
    data: config?.data,
  });
  const key =
    options?.dedupe === false
      ? `${baseKey} ${Date.now()} ${Math.random()}`
      : baseKey;

  return runReadWith429Retry(key, () => axios(config), options);
};

export const axiosGetRead = (url, config = {}, options) =>
  axiosRead({ ...config, method: "GET", url }, options);

export const fetchJsonRead = async (url, config = {}, options) => {
  const method = config?.method ?? "GET";
  const key = makeKey({ method, url, data: config?.body });

  return runReadWith429Retry(
    key,
    async () => {
      const response = await fetch(url, config);
      if (response.status === 429) {
        const err = new Error("Troppe richieste, riprova tra poco.");
        err.status = 429;
        err.headers = response.headers;
        throw err;
      }
      if (!response.ok) {
        const err = new Error(response.statusText || "Errore di caricamento");
        err.status = response.status;
        throw err;
      }
      return response.json();
    },
    options,
  );
};

export const isRateLimitError = (error) => getStatus(error) === 429;

export const friendlyReadError = (error, fallback = "Errore di caricamento") =>
  isRateLimitError(error)
    ? "Troppe richieste, riprova tra poco."
    : error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback;
